import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined'
import Chip from '@mui/material/Chip'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import useDialog from 'global/hooks/useDialog'
import useSuppressFlag from 'global/hooks/useSuppressFlag'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { LegData } from './TripDetails.types'

export interface DirectionsProps {
  leg: LegData
}

const Directions = ({ leg }: DirectionsProps): ReactElement | null => {
  const { t } = useTranslation()
  const { suppressFlagName, isSuppressed } = useSuppressFlag('google-maps-disclaimer')

  const openDirectionsUrl = useCallback(() => {
    const pickupStop = leg.wayPoints.find((waypoint) => waypoint.type === 'PUP')
    const deliveryStop = leg.wayPoints.find((waypoint) => waypoint.type === 'DEL')
    if (!pickupStop || !deliveryStop) {
      return
    }
    const pickupLocation = pickupStop.geoPoint
      ? `${pickupStop.geoPoint.latitude},${pickupStop.geoPoint.longitude}`
      : encodeURIComponent(
        [
          pickupStop.address.name.join(' '),
          pickupStop.address.street.join(', '),
          pickupStop.address.city.join(', '),
          pickupStop.address.zipCode,
          pickupStop.address.countryCode,
        ].join(', '),
      )
    const deliveryLocation = deliveryStop.geoPoint
      ? `${deliveryStop.geoPoint.latitude},${deliveryStop.geoPoint.longitude}`
      : encodeURIComponent(
        [
          deliveryStop.address.name.join(' '),
          deliveryStop.address.street.join(', '),
          deliveryStop.address.city.join(', '),
          deliveryStop.address.zipCode,
          deliveryStop.address.countryCode,
        ].join(', '),
      )
    window.open(
      `https://www.google.com/maps/dir/?api=1&origin=${pickupLocation}&destination=${deliveryLocation}&travelmode=driving&dir_action=navigate`,
      '_blank',
    )
    analyticsEvent('polestar_cs_leg_directions', [getRouteName(location.pathname)])
  }, [leg.wayPoints])
  const confirmationDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: openDirectionsUrl,
  })

  const handleDirectionsClick = useCallback(() => {
    if (isSuppressed) {
      openDirectionsUrl()
    } else {
      confirmationDialog.handleOpen()
    }
  }, [confirmationDialog.handleOpen, isSuppressed, openDirectionsUrl])

  return (
    <>
      <Chip
        icon={<DirectionsOutlinedIcon />}
        size="small"
        color="primary"
        variant="outlined"
        onClick={handleDirectionsClick}
        label={t('screens.cs.trip_details.directions.google_maps')}
      />

      <KNConfirmationDialog
        message={t('screens.cs.trip_details.directions.google_maps_disclaimer')}
        skipConfirmationFlag={suppressFlagName}
        open={confirmationDialog.isOpen}
        onAction={confirmationDialog.handleAction}
        onClose={confirmationDialog.handleClose}
      />
    </>
  )
}

export default Directions
